// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/context/auth-context/use-auth.ts';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AudioPresets,
  BackupCodecPolicy,
  LogLevel,
  RoomOptions,
  VideoPresets,
} from 'livekit-client';
import { API_BASE } from '@/config.ts';
import { avatarPlaceholder } from '@/lib/avatar-placeholder.ts';
import { getDeviceMetadata } from '@/components/device-metadata.tsx';
import type { ParticipantMetadata } from '@/types.ts';
import PresencePinger, { deviceID } from '@/components/presence/presence-pinger.tsx';
import { LiveKitRoom } from '@livekit/components-react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils.ts';
import { VideoConference } from '@/components/call/video-conference.tsx';
import { ControlBar } from '@/components/call/control-bar.tsx';
import { RecordingIndicator } from '@/components/call/recording-indicator.tsx';
import { AudioRenderer } from '@/components/call/audio-renderer.tsx';
import type { Call } from '@/context/call-context/call-context.tsx';
import { CallContext } from '@/context/call-context/call-context.tsx';
import { KeyboardShortcutsProvider } from '@/context/keyboard-shortcuts-context/keyboard-shortcuts-provider.tsx';
import { User, UserPlan, UserSettings } from '@/models';
import { useSettings } from '@/context/settings-context/settings-context.ts';
import { isPardusBrowser } from '@/components/check-user-agent.tsx';
import { KeyboardShortcuts } from '@/lib/livekit/KeyboardShortcuts.tsx';
import PingIndicator from '@/components/ping-indicator.tsx';
import { useScreenShareSettings } from '@/context/call-context/use-screen-share-settings.ts';
import { DebugMode } from '@/lib/livekit/Debug.tsx';
import { useBreakpoint } from '@/hooks/use-breakpoint.ts';
import { ScreenShareStateBridge } from '@/context/call-context/screen-share-state-bridge.tsx';
import { AudioAds } from '@/components/audio-ads.tsx';
import '@/styles/aads.css';
import { SettingsMenu } from '@/lib/livekit/SettingsMenu.tsx';
import { toast } from 'sonner';
import { ZeroPlanPopup } from '@/components/zero-plan-popup.tsx';
import { ServerSponsorBanner } from '@/components/server-sponsor-banner.tsx';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';

const SHOW_SETTINGS_MENU = 'true';

/**
 * MUST BE SINGLE INSTANCE to prevent blink on users connect/disconnect
 * @param children
 * @constructor
 */
export const CallProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { user } = useAuth();
  const [plan, setPlan] = useState<UserPlan | null>(UserPlan.FREE);
  const [userFromDb, setUserFromDb] = useState<User | null>(null);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
  const navigate = useNavigate();
  const { isMd: isDesktop } = useBreakpoint('md');
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const [mediaRoomContainer, setMediaRoomContainer] = useState<HTMLElement | null>(null);
  const [mediaRoomFloatingContainer, setMediaRoomFloatingContainer] = useState<HTMLElement | null>(
    null
  );
  const [docked, setDocked] = useState<boolean>(false); // docked = true means embeded in page. False - means element lives in sidebar
  const [microphoneActive, setMicrophoneActive] = useState<boolean>(true);
  const [token, setToken] = useState('');
  const [endCallClicked, setEndCallClicked] = useState(false);
  const showPrivateStatus = userSettings?.showStatusWhileInPrivateChannels === true;
  const [screenSharing, setScreenSharing] = React.useState(false);
  const [audioPreset, setAudioPreset] = useState(AudioPresets.music);
  const {
    selectedAudioOutputDeviceId,
    setSelectedAudioOutputDeviceId,
    autoGainControl,
    noiseSuppression,
    echoCancellation,
    selectedAudioInputDeviceId,
    setSelectedAudioInputDeviceId,
  } = useSettings();
  const volume = 100; // mock
  function setVolume() {} // mock
  const { selectedSharePreset, selectedEncoding } = useScreenShareSettings();
  const [serverAdsEnabled, setServerAdsEnabled] = useState(true);
  const [serverSponsor, setServerSponsor] = useState<any>(null);
  const [showSponsorBanner, setShowSponsorBanner] = useState(false);
  const socket = useSocket();

  const callRequestIdRef = useRef(0);
  // TODO: plan-based audioPreset to reduce bandwidth
  //       publishDefaults: {
  //         audioPreset: audioPreset || AudioPresets.music,
  /**
   * degradationPreference Controls how the encoder trades off between resolution and framerate when bandwidth is constrained.
   *
   * 'maintain-framerate': Prioritizes framerate, reduces resolution if needed
   * 'maintain-resolution': Prioritizes resolution, drops frames if needed
   * 'balanced': Balances between both
   * If not set, the SDK uses defaults based on track source:
   *
   * Camera: 'maintain-framerate' (smoother video for real-time communication)
   * Screen share: 'maintain-resolution' (clarity is critical for text/UI)
   * Other/unknown: 'balanced'
   */
  const liveKitOptions: RoomOptions = useMemo(
    () => ({
      // adaptiveStream: true,
      adaptiveStream: false, // Disable if causing track swaps on voice activity
      disconnectOnPageLeave: true,
      dynacast: false,
      // dynacast: false, // Consider disabling for spatial audio stability
      audioCaptureDefaults: {
        autoGainControl: autoGainControl,
        noiseSuppression: noiseSuppression,
        echoCancellation: echoCancellation,
        voiceIsolation: false,
        deviceId: selectedAudioInputDeviceId || 'default', // may cause call disconnect on device switch
      },
      videoCaptureDefaults: {
        resolution: VideoPresets.h720.resolution, // TODO: maybe reduce quality
      },
      // TODO: test, call
      // audioOutput: {
      //   deviceId: selectedAudioOutputDeviceId,
      // },
      publishDefaults: {
        dtx: true,
        red: false,
        audioPreset: audioPreset || AudioPresets.music,
        // forceStereo: true,
        videoCodec: 'vp9',
        backupCodec: { codec: 'vp8' },
        backupCodecPolicy: BackupCodecPolicy.PREFER_REGRESSION,
        // scalabilityMode: 'L3T3_KEY',
        // scalabilityMode: 'L1T3',
        screenShareEncoding: selectedEncoding,
        // degradationPreference: 'maintain-framerate', // games?
        // degradationPreference: 'maintain-resolution', // screenshare
        // degradationPreference: 'balanced',
        preConnectBuffer: true,
        simulcast: false, // disabled for SVC codecs (VP8 AV1)
      },
    }),
    [
      audioPreset,
      autoGainControl,
      noiseSuppression,
      echoCancellation,
      // selectedAudioInputDeviceId,
      // selectedAudioOutputDeviceId,
    ]
  );

  let ip = '127.0.0.1';

  const location = useLocation();

  // TODO: in gamesdb not work
  const isSidebarRoute =
    location.pathname.startsWith('/legal') ||
    location.pathname.startsWith('/channels') ||
    location.pathname.startsWith('/channels/@me') ||
    location.pathname.startsWith('/channels/@me/gamesdb') ||
    location.pathname.startsWith('/discovery');

  const normalizeUserSettings = (data: any): UserSettings =>
    ({
      telemetry: Boolean(data.telemetry),
      gameTracking: Boolean(data.gameTracking),
      weeklyEmails: Boolean(data.weeklyEmails),
      showStatusWhileInPrivateChannels: Boolean(data.showStatusWhileInPrivateChannels),
    }) as UserSettings;

  /**
   * Delay setting containers on page render
   *
   * */
  useEffect(() => {
    const updateMediaRoomContainer = () => {
      if (activeCall?.chatId) {
        const container = document.getElementById(
          `calls-media-room-container-${activeCall.chatId}`
        );
        const floating = document.getElementById(`calls-media-room-floating-container`);

        if (container) {
          setMediaRoomContainer(container);
        } else {
          setMediaRoomContainer(null);
        }
        if (floating) {
          setMediaRoomFloatingContainer(floating);
        } else {
          setMediaRoomFloatingContainer(null);
        }
      } else {
        setMediaRoomContainer(null);
        setMediaRoomFloatingContainer(null);
      }
    };

    const intervalId = setInterval(updateMediaRoomContainer, 200);

    return () => {
      clearInterval(intervalId);
    };
  }, [activeCall]);

  useEffect(() => {
    const outputDeviceId = selectedAudioOutputDeviceId;
    setSelectedAudioOutputDeviceId(outputDeviceId);
  }, [user?.id]);

  useEffect(() => {
    if (!user) {
      return;
    }

    fetch(`${API_BASE}/api/users/${user.id}`)
      .then((res) => res.json())
      .then((data) => {
        setPlan(data.plan);
        setUserFromDb(data);
        switch (plan) {
          case UserPlan.BASIC:
            setAudioPreset(AudioPresets.music);
            break;
          case UserPlan.OPTIMIM:
            setAudioPreset(AudioPresets.musicHighQuality);
            break;
          case UserPlan.EXTENDED:
            setAudioPreset(AudioPresets.musicHighQualityStereo);
            break;
          default:
            setAudioPreset(AudioPresets.music); // Free
            break;
        }
        // TODO: switch, now dont work, NOW always Free (music)
      })
      .catch((error) => {
        console.error(error);
      });
  }, [user]);

  useEffect(() => {
    if (!activeCall?.serverId) return;

    fetch(`${API_BASE}/api/servers/${activeCall.serverId}/ad-status`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((r) => r.json())
      .then((data) => {
        setServerAdsEnabled(data.adsEnabled);
        setServerSponsor(data.sponsor);
      })
      .catch(console.error);
  }, [activeCall?.serverId]);

  useEffect(() => {
    if (!activeCall || serverAdsEnabled || !serverSponsor) {
      setShowSponsorBanner(false);
      return;
    }

    setShowSponsorBanner(true);

    const timer = setTimeout(() => {
      setShowSponsorBanner(false);
    }, 30_000);

    return () => clearTimeout(timer);
  }, [activeCall?.serverId, serverAdsEnabled, serverSponsor]);

  /**
   * Called synchronously in client elements on button click
   * @param call
   */
  const startCall = (call: Call) => {
    // Сначала инвалидируем старую попытку, если звонок уже активен.
    if (activeCall?.token) {
      endCall();
    }

    // У каждой попытки подключения свой идентификатор.
    const requestId = ++callRequestIdRef.current;

    const channelType = call.channelType;

    const fetchToken = (room: string) => {
      if (!user?.name) return;
      const name = `${user?.nickname || user.name || user.email || user.phone}`;
      const id = user.id;
      const avatar = userFromDb?.image || user.image || avatarPlaceholder({ name: name });
      const role = call.role;
      const serverOwnerId = call.serverOwnerId;
      const isOwner = serverOwnerId === id;

      const deviceData = getDeviceMetadata(navigator.userAgent);
      const {
        deviceType,
        deviceModel,
        deviceVendor,
        engineName,
        engineVersion,
        osName,
        osVersion,
        browserVersion,
        arch,
      } = deviceData;
      let { browserName } = deviceData;

      const isPardus = isPardusBrowser();
      if (isPardus) {
        browserName += '_PardusBrowser';
      }

      // TO prevent multi-tab call, remove deviceID. If we want multi-tabs calls -> add DeviceId in metadata
      const metadata: ParticipantMetadata = {
        id,
        role,
        avatar,
        ip,
        deviceId: deviceID,
        deviceType,
        deviceModel,
        deviceVendor,
        engineName,
        engineVersion,
        osName,
        osVersion,
        browserName,
        browserVersion,
        arch,
      };

      const queryParam = `?channelType=${encodeURIComponent(channelType)}&owner=${isOwner}`; // OWNER check is bad, used only for tribune mode

      fetch(`${API_BASE}/api/tkit/get-participant-token${queryParam}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          room,
          username: name,
          metadata: JSON.stringify(metadata),
        }),
      })
        .then(async (resp) => {
          const data = await resp.json();

          if (!resp.ok) {
            throw data;
          }

          return data;
        })
        .then((data) => {
          // Ответ от предыдущего подключения больше не актуален.
          if (requestId !== callRequestIdRef.current) {
            console.debug('[CallProvider] Ignoring stale token response', {
              requestedChannelId: call.chatId,
              requestId,
              currentRequestId: callRequestIdRef.current,
            });
            return;
          }

          setToken(data.token);
          call.name = name;
          call.token = data.token;
          call.tkitServerUrl = data.serverUrl;
          call.tkitServerId = data.serverId;
          call.tkitServerName = data.serverName;
          setActiveCall(call);

          fetch(`${API_BASE}/api/push/send-to-server`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              serverId: call.serverId,
              channelId: call.chatId,
              channelName: call.channelName,
              userName: name,
            }),
          }).catch((err) => {
            console.error('Failed to notify server about join (push):', err);
          });
        })
        .catch((err) => {
          if (requestId !== callRequestIdRef.current) {
            return;
          }

          endCall();

          if (err?.error === 'BOOST_REQUIRED') {
            toast.info(
              'Пробный период истек. Для подключения к голосовому каналу требуется подписка на Boost'
            );

            navigate(
              '/channels/@me/boost?msg=Для%20подключения%20к%20каналу%20требуется%20подписка'
            );
            return;
          }

          toast.error('Невозможно начать звонок');
        });
    };

    /**
     * @experimental may overload API
     */
    const fetchUserSettings = async () => {
      if (!user?.id) return;

      try {
        const response = await fetch(`${API_BASE}/api/user-settings`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        setUserSettings(normalizeUserSettings(data)); // only for toggle show in private channels
      } catch (error) {
        console.error('Error fetching userSettings:', error);
      }
    };
    // fetchUserSettings();

    fetchToken(call.chatId);
  };

  const endCall = () => {
    // Инвалидируем незавершённые запросы получения токена.
    ++callRequestIdRef.current;

    if (screenSharing && user?.id && activeCall) {
      const stopScreenSharingMessage = JSON.stringify({
        type: 'stop-screen-sharing',
        meta: {
          channelId: activeCall.chatId,
          userId: user.id,
          participantIdentity: activeCall.name || user.name || user.id,
        },
      });
      socket?.send(stopScreenSharingMessage);
    }
    if (user?.id && activeCall) {
      fetch(`${API_BASE}/api/channel-activity/system`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channelId: activeCall.chatId,
          serverId: activeCall.serverId,
          action: 'leave',
        }),
      }).catch((err) => console.error('Failed to log channel leave:', err));
    }

    setActiveCall(null);
    setToken('');
  };

  const toggleDock = () => {
    if (!activeCall) return;
    if (!token) return;

    setDocked(!docked);
  };

  const goToCall = () => {
    navigate(`/servers/${activeCall?.serverId}/channels/${activeCall?.chatId}`);
  };

  const isCurrentPageActiveCall = () => {
    const pathnameSplit = window.location.pathname.split('/');
    const channelIdx = pathnameSplit.indexOf('channels');
    return channelIdx > -1 && pathnameSplit[channelIdx + 1] === activeCall?.chatId;
  };

  const handleDisconnected = () => {
    endCall();
  };

  /**
   * @experimental
   */
  const resetCallState = () => {
    setActiveCall(null);
    setDocked(false);
    setEndCallClicked(true);
    setMicrophoneActive(true);
  };

  /**
   * @experimental, API is broken.
   * NOT IMPLEMENTED
   * @param userId
   * @param channelId
   * @param serverId
   */
  async function kickUser(userId: string, channelId: string, serverId: string) {
    return;
    // NOT IMPLEMENTED
    // try {
    //   await fetch(`${API_BASE}/api/tkit/remove-participant`, {
    //     method: 'POST',
    //     headers: { 'Content-Type': 'application/json' },
    //     body: JSON.stringify({
    //       roomId: channelId,
    //       userId: userId,
    //       serverId: serverId,
    //     }),
    //   });
    // } catch (error) {
    //   console.log(error);
    // }
  }

  useEffect(() => {
    const handleMoveUser = (event: Event) => {
      const customEvent = event as CustomEvent<{
        serverId: string;
        destChannelId: string;
      }>;

      const { serverId, destChannelId } = customEvent.detail ?? {};

      if (!serverId || !destChannelId) return;

      const destination = `/servers/${serverId}/channels/${destChannelId}`;

      if (window.location.pathname === destination) return;

      // Сбросить текущее состояние звонка.
      // Если LiveKitRoom рендерится условно по activeCall/token,
      // это приведёт к его размонтированию.
      endCall();
      setDocked(false);
      setScreenSharing(false);
      setMicrophoneActive(true);

      navigate(destination, {
        replace: false,
        state: {
          fromPreview: true,
          movedByAdmin: true,
          moveTimestamp: Date.now(),
        },
      });
    };

    const handleChannelEdit = (event: Event) => {
      const customEvent = event as CustomEvent<{ channelId: string; channelType: string }>;
      const { channelId, channelType } = customEvent.detail ?? {};

      if (!channelId || !channelType || !activeCall) return;

      // Обновляем только если событие для текущего канала
      if (channelId !== activeCall.chatId) return;

      setActiveCall((prev) =>
        prev
          ? {
              ...prev,
              channelType: channelType as Call['channelType'],
            }
          : prev
      );
    };

    window.addEventListener('calls:move-user-to-channel', handleMoveUser);
    window.addEventListener('calls:channel-edit', handleChannelEdit);

    return () => {
      window.removeEventListener('calls:move-user-to-channel', handleMoveUser);
      window.removeEventListener('calls:channel-edit', handleChannelEdit);
    };
  }, [activeCall?.chatId, activeCall, endCall, navigate, setDocked]);

  return (
    <CallContext.Provider
      value={{
        activeCall,
        startCall,
        endCall,
        toggleDock,
        docked,
        setDocked,
        goToCall,
        isCurrentPageActiveCall,
        microphoneActive,
        setMicrophoneActive,
        endCallClicked,
        setEndCallClicked,
        kickUser,
        volume,
        setVolume,
        resetCallState,
        screenShare: {
          isSharing: screenSharing,
        },
        setScreenSharing,
      }}
    >
      <KeyboardShortcutsProvider>
        {children}
        {activeCall && (
          <>
            <PresencePinger
              userId={activeCall.userId}
              serverId={activeCall.serverId}
              channelId={activeCall.chatId}
              serverName={!showPrivateStatus && activeCall.private ? '' : activeCall.serverName}
              channelName={!showPrivateStatus && activeCall.private ? '' : activeCall.channelName}
              channelType={activeCall.channelType}
              avatar={user?.image || activeCall?.avatar || avatarPlaceholder({ name: user?.name })}
              name={user?.name || activeCall?.name || 'undefined'}
            />
            {/*{activeCall && <AudioAds plan={plan} user={userFromDb} />}*/}
            {activeCall && serverAdsEnabled && <AudioAds plan={plan} user={userFromDb} />}
            {activeCall && serverAdsEnabled && <ZeroPlanPopup userId={userFromDb?.id} />}
            {showSponsorBanner && (
              <div className="fixed top-12 left-1/2 z-50 -translate-x-1/2">
                <ServerSponsorBanner
                  sponsor={serverSponsor}
                  onClose={() => setShowSponsorBanner(false)}
                />
              </div>
            )}

            {/*{activeCall && <RecordingWarning key={activeCall.token} />}*/}
            <LiveKitRoom
              key={`${activeCall.chatId}:${activeCall.token}`}
              hidden
              video={activeCall?.video || false}
              audio={activeCall?.audio || true}
              token={activeCall.token}
              serverUrl={activeCall.tkitServerUrl}
              connect={true}
              data-lk-theme="default"
              options={liveKitOptions}
            >
              <KeyboardShortcuts />
              <ScreenShareStateBridge />
              {(mediaRoomContainer || mediaRoomFloatingContainer) &&
                createPortal(
                  <>
                    {!token ? (
                      <div className="flex h-full flex-1 flex-col items-center justify-center">
                        <Loader2 className="my-4 size-7 animate-spin text-zinc-500" />
                      </div>
                    ) : (
                      <>
                        <div
                          className={cn(
                            'flex-1 flex flex-col h-full min-h-0 overflow-hidden',
                            docked ? '' : 'w-full'
                          )}
                          onContextMenu={(e) => {
                            e.preventDefault();
                          }}
                        >
                          {docked && (
                            <VideoConference
                              key={activeCall.chatId}
                              chatId={activeCall?.chatId}
                              onEndCall={endCall}
                              onDisconnected={handleDisconnected}
                              channelType={activeCall.channelType}
                              SettingsComponent={SHOW_SETTINGS_MENU ? SettingsMenu : undefined}
                              plan={plan}
                              serverId={activeCall.serverId}
                            />
                          )}
                          <div className="mt-2 flex flex-col " style={{}}>
                            {!docked && (
                              <>
                                {isPardusBrowser() ? (
                                  <div className="mx-2 wave-container flex items-center">
                                    <div className="wave-bar bg-green-500 dark:bg-green-400"></div>
                                    <div className="wave-bar bg-green-500 dark:bg-green-400"></div>
                                    <div className="wave-bar bg-green-500 dark:bg-green-400"></div>
                                    <div className="wave-bar bg-green-500 dark:bg-green-400"></div>
                                    <div className="wave-bar bg-green-500 dark:bg-green-400"></div>
                                    {/*<MicVisualizer />*/}
                                    <span className="ml-1 text-xs font-light text-green-500">
                                      Подключено
                                      <PingIndicator url={activeCall.tkitServerUrl} />
                                    </span>
                                    <span className="text-[10px] uppercase font-medium text-muted-foreground">
                                      {activeCall.tkitServerName}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="wave-container ml-2">
                                    <div className="wave-bar bg-green-500 dark:bg-green-400"></div>
                                    <div className="wave-bar bg-green-500 dark:bg-green-400"></div>
                                    <div className="wave-bar bg-green-500 dark:bg-green-400"></div>
                                    <div className="wave-bar bg-green-500 dark:bg-green-400"></div>
                                    <div className="wave-bar bg-green-500 dark:bg-green-400"></div>
                                    <span className="text-xs font-light text-green-500">
                                      Подключено <PingIndicator url={activeCall.tkitServerUrl} />
                                    </span>
                                    <span className="text-[10px] uppercase font-medium text-muted-foreground">
                                      {activeCall.tkitServerName}
                                    </span>
                                  </div>
                                )}
                              </>
                            )}
                            <div className="ml-2 flex w-full flex-row items-center justify-between gap-4">
                              {!docked && (
                                <div className="flex items-center justify-center">
                                  <Link
                                    to={`/servers/${activeCall.serverId}/channels/${activeCall.chatId}`}
                                    className="flex flex-row items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-blue-500"
                                  >
                                    {activeCall.channelName?.length > 10
                                      ? `${activeCall.channelName?.slice(0, 10)}...`
                                      : activeCall.channelName}{' '}
                                    {'/'} {activeCall.serverName}
                                  </Link>
                                </div>
                              )}
                              {!docked && <div className="grow" />}
                              <div
                                className={`${!isDesktop ? 'mx-auto mb-10 md:mb-16 flex flex-col items-center justify-center' : 'mx-auto'}`}
                              >
                                <ControlBar
                                  controls={{ chat: false, settings: false }}
                                  serverId={activeCall.serverId}
                                  chatId={activeCall.chatId}
                                  onEndCall={endCall}
                                  channelType={activeCall.channelType}
                                  plan={plan}
                                />
                              </div>
                              {docked && <DebugMode logLevel={LogLevel.silent} />}
                              <RecordingIndicator />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </>,
                  docked && mediaRoomContainer && !isSidebarRoute
                    ? mediaRoomContainer
                    : mediaRoomFloatingContainer! // fallback if not docked
                )}
              {/*<RecordingIndicator />*/}
              <AudioRenderer plan={plan} />
            </LiveKitRoom>
          </>
        )}
      </KeyboardShortcutsProvider>
    </CallContext.Provider>
  );
};
